import { apiClient } from './client';
import { convertKeysToCamelCase } from '../lib/utils';
import { normalizeCitations } from '../chat/useChat';
import type { ChatMessage, Citation, ThinkingSection } from '../chat/types';

export interface ChatThreadSummary {
  chatId: string;
  /** Set client-side from the fetchChatList argument — backend omits it from the response body. */
  chatType: 'research' | 'word_edit' | 'draft';
  name: string;
  caseId: string | null;
  isPinned: boolean;
  createdAt: string;
  updatedAt: string;
  /** Free-form chat row metadata — typically carries `s3_key`, `latest_version`,
   *  `docx_sha256`, `source`, `status`, etc. Populated by the backend jsonb
   *  column; the add-in uses it to locate the latest DOCX without walking
   *  message history. */
  metadata?: Record<string, unknown>;
}

interface RawMessageRecord {
  messageId?: string;
  message_id?: string;
  chatId?: string;
  chat_id?: string;
  role?: string;
  content?: string;
  text?: string;
  metadata?: Record<string, unknown>;
  citations?: Citation[];
  createdAt?: string | number;
  created_at?: string | number;
  timestamp?: string | number;
  shouldShowMessage?: boolean;
  should_show?: boolean;
  chatStatus?: string;
  chat_status?: string;
  infoType?: string;
  info_type?: string;
  changeId?: string;
  change_id?: string;
  feedback?: string;
}

/**
 * List chats of a given type. Two flavors mapped to backend chat types:
 *   - 'research'  → unified research threads from web + Word
 *   - 'word_edit' → Word-add-in edit sessions only
 *
 * Backend endpoint: GET /api/v1/chat?chat_type=X&limit=N&offset=0
 * The response omits chat_type (it's implicit in the query), so we stamp it
 * back on each entry client-side for downstream consumers.
 */
/**
 * Walk the chat's most-recent messages to find the newest version pointers.
 * Returns `{s3Key, version, sha256}` — any of which may be null if the chat
 * hasn't produced a DOCX yet. Used by the outdated-copy detection (Phase E).
 */
export async function fetchLatestDocxVersion(chatId: string): Promise<{
  s3Key: string | null;
  version: string | null;
  sha256: string | null;
}> {
  const res = await apiClient.get('/api/v1/chat/messages', {
    params: { chatID: chatId, limit: 50, offset: 0 },
  });
  const body = res.data as
    | { data?: { chats?: RawMessageRecord[]; messages?: RawMessageRecord[] } }
    | undefined;
  const raw = body?.data?.chats ?? body?.data?.messages ?? [];
  // Messages typically arrive newest-first; walk from newest forward.
  for (const m of raw) {
    const meta = (m.metadata ?? {}) as {
      s3_key?: string;
      latest_version?: string;
      docx_sha256?: string;
    };
    if (typeof meta.s3_key === 'string' && meta.s3_key) {
      return {
        s3Key: meta.s3_key,
        version: typeof meta.latest_version === 'string' ? meta.latest_version : null,
        sha256: typeof meta.docx_sha256 === 'string' ? meta.docx_sha256 : null,
      };
    }
  }
  return { s3Key: null, version: null, sha256: null };
}

/**
 * Duplicate an existing chat + all messages into a new chat with a
 * different chat_type. Used when the user switches mode inside a
 * non-empty thread (e.g. research → edit / draft) — the original thread
 * stays untouched and the new forked chat carries every prior message
 * for context. Returns the new `chat_id`.
 */
export async function forkChat(args: {
  sourceChatId: string;
  newChatType: 'research' | 'word_edit' | 'draft';
  name?: string;
}): Promise<{ chatId: string; messagesCount: number }> {
  const res = await apiClient.post('/api/v1/chat/fork', {
    source_chat_id: args.sourceChatId,
    new_chat_type: args.newChatType,
    name: args.name,
    source: 'word_addon',
  });
  const body = res.data as {
    data?: { chat_id?: string; messages_count?: number };
  } | undefined;
  const chatId = body?.data?.chat_id;
  if (!chatId) throw new Error('Fork response missing chat_id');
  return {
    chatId,
    messagesCount: Number(body?.data?.messages_count ?? 0),
  };
}

/**
 * F6 + F7 — partial update of chat row metadata (pin state, title, etc.).
 *
 * Backend endpoint: PUT /api/v1/chat
 * Body shape per `Backend/models/chat_models.go:81-89` (EditChatRequest):
 *   - chat_id: required
 *   - title?: *string (omit to leave unchanged)
 *   - is_pinned?: *bool (omit to leave unchanged)
 *   - is_user_renamed?: *bool — set true when title is a user-typed rename so
 *     server-side auto-summarisers don't overwrite it later
 *   - status?, case_id?, metadata?: also supported by the schema, not used here
 *
 * SOURCE: frontend-prod uses the same endpoint shape via
 *   `frontend-prod/app/store/thunks/chatThunk.ts` (verified against backend model).
 */
export async function updateChatMeta(args: {
  chatId: string;
  title?: string;
  isPinned?: boolean;
  isUserRenamed?: boolean;
}): Promise<void> {
  const body: Record<string, unknown> = { chat_id: args.chatId };
  if (typeof args.title === 'string') body.title = args.title;
  if (typeof args.isPinned === 'boolean') body.is_pinned = args.isPinned;
  if (typeof args.isUserRenamed === 'boolean') body.is_user_renamed = args.isUserRenamed;
  await apiClient.put('/api/v1/chat', body);
}

export async function fetchChatList(
  chatType: 'research' | 'word_edit' | 'draft',
  opts: { limit?: number; source?: string } = {},
): Promise<ChatThreadSummary[]> {
  const { limit = 20, source } = opts;
  const params: Record<string, string | number> = {
    chat_type: chatType,
    limit,
    offset: 0,
  };
  if (source) params.source = source;
  const res = await apiClient.get('/api/v1/chat', { params });
  const body = res.data as { data?: { chats?: unknown[] } } | undefined;
  const raw = body?.data?.chats ?? [];
  const converted = convertKeysToCamelCase<Omit<ChatThreadSummary, 'chatType'>[]>(raw);
  return converted.map((t) => ({ ...t, chatType }));
}

/**
 * Load all messages for a chat and map them into our ChatMessage shape.
 *
 * Backend endpoint: GET /api/v1/chat/messages?chatID=X&limit=N&offset=0
 * Response shape: { data: { chats: MessageWithChatStatus[] } }
 *   (the outer key is "chats" even though the payload is messages — backend quirk)
 *
 * Per-message fields (see Backend/models/chat_models.go:MessageWithChatStatus):
 *   - message_id, chat_id
 *   - role: "user" | "ai"  (not "assistant" — backend-specific)
 *   - content: string
 *   - timestamp: RFC3339 string
 *   - metadata: { citations?, thinking?, change_id?, info_type?, ... }
 *   - should_show: bool
 *
 * Maps to our ChatMessage:
 *   - role: "user"→"human", "ai"→"llm"
 *   - text ← content
 *   - createdAt ← timestamp (ms since epoch)
 *   - thinking/citations extracted from metadata
 */
export async function fetchChatMessages(chatId: string, limit = 200): Promise<ChatMessage[]> {
  const res = await apiClient.get('/api/v1/chat/messages', {
    params: { chatID: chatId, limit, offset: 0 },
  });
  const body = res.data as
    | { data?: { chats?: RawMessageRecord[]; messages?: RawMessageRecord[] } }
    | undefined;
  // Prefer "chats" (current backend shape); fall back to "messages" for safety.
  const raw = body?.data?.chats ?? body?.data?.messages ?? [];

  const mapped = raw
    .filter((m) => {
      // Honor backend's should_show flag — skip hidden system messages
      const show = m.shouldShowMessage ?? m.should_show;
      return show !== false;
    })
    .map((m, idx): ChatMessage => {
      const messageId = m.messageId ?? m.message_id ?? `msg-${idx}-${Date.now()}`;
      const role = normalizeRole(m.role);
      const text = m.content ?? m.text ?? '';
      const createdAt = toEpochMs(m.timestamp ?? m.createdAt ?? m.created_at);
      const metadata = m.metadata ?? {};
      const citations = extractCitations(metadata, m.citations);
      const thinking = extractThinking(metadata);
      const rawInfoType = m.infoType ?? m.info_type ?? (metadata as { info_type?: string }).info_type;
      const infoType =
        rawInfoType === 'applied' || rawInfoType === 'rejected' || rawInfoType === 'retried'
          ? (rawInfoType as 'applied' | 'rejected' | 'retried')
          : undefined;
      const changeId =
        m.changeId ?? m.change_id ?? (metadata as { change_id?: string }).change_id ?? undefined;

      const feedback =
        m.feedback === 'Liked' || m.feedback === 'Disliked'
          ? (m.feedback as 'Liked' | 'Disliked')
          : null;

      const mMeta = metadata as {
        s3_key?: string;
        latest_version?: string;
        edit_count?: number;
      };
      const editedS3Key = typeof mMeta.s3_key === 'string' ? mMeta.s3_key : undefined;
      const editCount =
        typeof mMeta.edit_count === 'number' ? mMeta.edit_count : undefined;

      return {
        id: messageId,
        role,
        text,
        createdAt,
        streaming: false,
        editedS3Key,
        editCount,
        thinking: thinking.length ? thinking : undefined,
        citations: citations.length ? citations : undefined,
        changeId,
        infoType,
        feedback,
      };
    });

  // Backend usually returns newest→oldest; sort ascending so the UI renders
  // in chronological order like the live stream.
  return mapped.sort((a, b) => (a.createdAt ?? 0) - (b.createdAt ?? 0));
}

function normalizeRole(r: string | undefined): ChatMessage['role'] {
  if (!r) return 'llm';
  const v = r.toLowerCase();
  if (v === 'user' || v === 'human') return 'human';
  if (v === 'info' || v === 'system') return 'info';
  if (v === 'change') return 'change';
  // Backend stores AI messages with role "ai"; also accept "assistant"/"llm".
  return 'llm';
}

function toEpochMs(v: unknown): number {
  if (typeof v === 'number') {
    // Accept both seconds and ms epoch
    return v > 1e12 ? v : v * 1000;
  }
  if (typeof v === 'string' && v) {
    const parsed = new Date(v).getTime();
    return Number.isFinite(parsed) ? parsed : Date.now();
  }
  return Date.now();
}

function extractCitations(metadata: Record<string, unknown>, fallback?: Citation[]): Citation[] {
  const fromMeta = (metadata as { citations?: { citations?: unknown[] } | unknown[] }).citations;
  if (Array.isArray(fromMeta)) return normalizeCitations(fromMeta);
  if (fromMeta && Array.isArray((fromMeta as { citations?: unknown[] }).citations)) {
    return normalizeCitations((fromMeta as { citations: unknown[] }).citations);
  }
  if (Array.isArray(fallback)) return normalizeCitations(fallback);
  return [];
}

function extractThinking(metadata: Record<string, unknown>): ThinkingSection[] {
  const t = (metadata as { thinking?: ThinkingSection[] | string }).thinking;
  if (Array.isArray(t)) return t;
  return [];
}
